import { Box, Button, IconButton, Typography } from '@mui/material';
import {
  QuestionContentModel,
  ResourceCategoryEnum,
} from '@protos/content_management/content.db_pb';
import { ReactNode, useEffect, useState } from 'react';
import { pxToRem, pxTovW } from '../../../../commonUtils/resizeUtils';
import { IStyles } from '../../../../commonUtils/styleUtils';
import PrimaryButton from '../../../elements/PrimaryButton';
import { SelectedResourceInfo } from '../../VideoPlayerWrapper/VideoPlayerWrapper';
import Question from '../Question';
import { QuestionEventCollection } from '../QuestionContainerWithSolution';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { PreviouslyUsedTag } from '../../../elements/PreviouslyUsedTag';

export interface QuestionContainerProps {
  questionContent?: QuestionContentModel;
  questionNumber?: number | string;
  disableInput?: boolean;
  userAttemptedAnswer?: string[];
  handleAnswerChange?: (answer: string, index?: number) => void;
  onDisableButtonClick?: () => void;
  isCorrect?: boolean;
  showAnswer?: boolean;
  handleNext?: () => void;
  showNextButton?: boolean;
  showSubmitButton?: boolean;
  headerRightElement?: JSX.Element;
  headerLeftElement?: JSX.Element;
  isSubmitted?: boolean;
  hideMarks?: boolean;
  homeworkAttempt?: boolean;
  scanClickHandler?: () => void;
  selected_resource_info?: SelectedResourceInfo;
  onNextButtonClick?: () => void;
  eventCollection?: QuestionEventCollection;
  resourceCategory?: ResourceCategoryEnum;
  imageResizeEnabled?: boolean;
  hideImage?: boolean;
  customMarks?: boolean;
  clearAnswerDisplay?: boolean;
  isClearAnswerDisabled?: boolean;
  clearAnswerClickHandler?: () => void;
  onMarksChange?: (newMarks: number) => void;
  hiddenScanButton?: boolean;
  isPalQuestion?: boolean;
  renderPalMcqInstructionBanner?: (isMultiple: boolean) => ReactNode;
  isPreviouslyUsed?: boolean;
}

const styles: IStyles = {
  questionDetailsWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: {
      xs: pxToRem(15),
      md: pxTovW(15),
    },
    // justifyContent: 'flex-end',
    '& > :last-child': {
      marginLeft: 'auto',
    },
  },
  marksWrapper: {
    padding: {
      xs: `${pxToRem(5)} ${pxToRem(13)}`,
      md: `${pxTovW(7)} ${pxTovW(11)}`,
    },
    borderRadius: {
      xs: pxToRem(16),
      md: pxTovW(35),
    },
    border: '1px solid #CEEAFF',
    marginLeft: '10px',
    display: 'flex',
    alignItems: 'center',
  },
  marks: {
    fontWeight: 700,
  },
};

export default function QuestionContainer(props: QuestionContainerProps) {
  const {
    questionContent,
    hideMarks,
    questionNumber,
    disableInput,
    handleAnswerChange,
    userAttemptedAnswer,
    handleNext,
    showNextButton,
    showSubmitButton,
    isSubmitted,
    homeworkAttempt,
    scanClickHandler,
    selected_resource_info,
    onNextButtonClick,
    eventCollection,
    resourceCategory,
    imageResizeEnabled,
    hideImage,
    customMarks,
    clearAnswerDisplay,
    isClearAnswerDisabled,
    clearAnswerClickHandler,
    onMarksChange,
    hiddenScanButton,
    isPalQuestion,
    renderPalMcqInstructionBanner,
    isPreviouslyUsed,
  } = props;
  const MIN_MARKS = 1;
  const MAX_MARKS = 20;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ------------------------------------------------------------

  /** Whole number when exact; otherwise up to two decimal places (e.g. 1, 1.5, 1.33). */
  const formatMarksDisplay = (value: number): string => {
    const rounded = Math.round(value * 100) / 100;
    return Number.isInteger(rounded)
      ? String(rounded)
      : parseFloat(rounded.toFixed(2)).toString();
  };

  // ------------------------------------------------------------

  if (!questionContent) {
    return null;
  }

  const questionCase = questionContent.model.case;
  if (!questionCase) {
    return null;
  }

  // ------------------------------------------------------------

  const marks =
    questionContent.model.value.commonQuestionContent?.marks.reduce(
      (acc, curr) => acc + curr,
      0
    ) || 0;

  const [dynamicMarks, setDynamicMarks] = useState<number>(marks);

  // ------------------------------------------------------------

  const handleMarksChange = (delta: number) => {
    // const updated = Math.max(1, dynamicMarks + delta);
    const updated = Math.min(
      MAX_MARKS,
      Math.max(MIN_MARKS, dynamicMarks + delta)
    );
    setDynamicMarks(updated);
    onMarksChange?.(updated);
  };

  // ------------------------------------------------------------

  const marksDisplay = formatMarksDisplay(marks);

  const dynamicMarksDisplay = formatMarksDisplay(dynamicMarks);

  // ------------------------------------------------------------

  const handleClick = async () => {
    try {
      setLoading(true);
      setError(null);
      if (handleNext) {
        await handleNext();
      }
    } catch (error) {
      setError('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------------------------

  useEffect(() => {
    setDynamicMarks(marks);
  }, [marks]);

  // ------------------------------------------------------------

  return (
    <Box>
      <Box sx={styles.questionDetailsWrapper}>
        {!!questionNumber && (
          <Typography variant="elementH3">Q. {questionNumber}</Typography>
        )}
        {!!marks && !hideMarks && (
          <>
            {customMarks ? (
              <Box
                sx={{
                  // width: pxTovW(113),
                  marginX: pxToRem(10),
                  height: 'auto',
                  boxShadow: '0px 3px 6px #0000000A',
                  border: '1px solid #EEEEEE',
                  borderRadius: '8px',
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => handleMarksChange(-1)}
                  disabled={dynamicMarks <= MIN_MARKS}
                >
                  <RemoveIcon
                    fontSize="small"
                    sx={{
                      color:
                        dynamicMarks <= MIN_MARKS
                          ? '#BDBDBD'
                          : 'secondary.main',
                    }}
                  />
                </IconButton>
                <Typography
                  variant="smallText"
                  fontFamily="Poppins"
                  sx={styles.marks}
                >
                  {dynamicMarksDisplay}{' '}
                  {dynamicMarksDisplay === '1' ? 'Mark' : 'Marks'}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleMarksChange(1)}
                  disabled={dynamicMarks >= MAX_MARKS}
                >
                  <AddIcon
                    fontSize="small"
                    sx={{
                      color:
                        dynamicMarks >= MAX_MARKS
                          ? '#BDBDBD'
                          : 'secondary.main',
                    }}
                  />
                </IconButton>
              </Box>
            ) : (
              <Box
                sx={{
                  ...styles.marksWrapper,
                  marginX: pxToRem(10),
                }}
              >
                <Typography
                  variant="elementSmallText"
                  fontFamily="Poppins"
                  sx={styles.marks}
                >
                  {marksDisplay} {marksDisplay === '1' ? 'Mark' : 'Marks'}
                </Typography>
              </Box>
            )}
            {clearAnswerDisplay && (
              <Box
                component="button"
                sx={{
                  background: '#CDFFE3',
                  border: '1px solid #0AA34F',
                  borderRadius: { xs: pxToRem(35), md: pxTovW(35) },
                  width: { xs: pxToRem(60), md: pxTovW(90), lg: pxTovW(80) },
                  height: { xs: pxToRem(25), md: pxTovW(60), lg: pxTovW(40) },
                  opacity: '1',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  '&:hover': {
                    cursor: 'pointer',
                  },
                  '&:disabled': {
                    background: '#EEEEEE',
                    border: '1px solid #BDBDBD',
                  },
                }}
                disabled={isClearAnswerDisabled}
                onClick={clearAnswerClickHandler}
              >
                <Typography variant="smallText" fontFamily="Poppins">
                  Clear
                </Typography>
              </Box>
            )}
          </>
        )}
        {isPreviouslyUsed && <PreviouslyUsedTag />}
        <Box>{props.headerLeftElement}</Box>
        <Box>{props.headerRightElement}</Box>
      </Box>
      <Question
        hiddenScanButton={hiddenScanButton}
        questionContent={questionContent}
        disableInput={disableInput}
        userAttemptedAnswer={userAttemptedAnswer}
        handleAnswerChange={handleAnswerChange}
        homeworkAttempt={homeworkAttempt && homeworkAttempt}
        scanClickHandler={scanClickHandler}
        selected_resource_info={selected_resource_info}
        eventCollection={eventCollection}
        resourceCategory={resourceCategory}
        imageResizeEnabled={imageResizeEnabled}
        hideImage={hideImage}
        isPalQuestion={isPalQuestion}
        renderPalMcqInstructionBanner={renderPalMcqInstructionBanner}
      />
      {[
        'fibContentModel',
        'mcqMultipleContentModel',
        'veryShortQuesContentModel',
        'shortQuesContentModel',
        'longQuesContentModel',
        'cbqQuesContentModel',
      ].includes(questionCase) &&
        handleNext &&
        showNextButton && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ marginTop: { xs: pxToRem(10), md: pxTovW(10) } }}
          >
            <PrimaryButton
              onClick={() => {
                handleClick();
                if (onNextButtonClick) onNextButtonClick();
              }}
              disabled={
                loading ||
                !userAttemptedAnswer ||
                userAttemptedAnswer.length === 0 ||
                //for subjective questions
                (userAttemptedAnswer.length === 1 &&
                  !userAttemptedAnswer[0].trim())
              }
            >
              Next
            </PrimaryButton>
          </Box>
        )}
      {showSubmitButton && handleNext && !isSubmitted && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent={isPalQuestion ? 'flex-end' : 'center'}
          sx={{ marginTop: { xs: pxToRem(20), md: pxTovW(20) } }}
        >
          {isPalQuestion ? (
            <Button
              variant="contained"
              onClick={() => {
                handleClick();
                eventCollection?.submitAnswer &&
                  eventCollection?.submitAnswer();
              }}
              disabled={
                loading ||
                !userAttemptedAnswer ||
                userAttemptedAnswer.length === 0
              }
              sx={{
                background: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)',
                color: 'white',
                px: 5,
                py: 1.25,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: 15,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                boxShadow: '0 4px 16px rgba(76,175,80,0.3)',
                '&:hover': {
                  background:
                    'linear-gradient(135deg, #4caf50 0%, #43a047 100%)',
                  boxShadow: '0 8px 24px rgba(76,175,80,0.4)',
                },
                '&:disabled': {
                  background: '#d1d5db',
                  color: '#9ca3af',
                  boxShadow: 'none',
                },
              }}
            >
              SUBMIT
            </Button>
          ) : (
            <PrimaryButton
              sx={{
                width: {
                  xs: pxToRem(120),
                  md: pxTovW(170),
                },
                padding: { md: pxToRem(15) },
                height: { xs: pxToRem(38), md: pxTovW(56) },
                fontFamily: 'Poppins',
                fontWeight: 600,
                fontSize: { xs: '16px', md: '18px', lg: '21px' },
                boxShadow:
                  'inset 0 0 8px rgba(0, 0, 0, 0.5),0 7px 13px rgba(0, 0, 0, 0.5)',
                borderRadius: '10px',
              }}
              onClick={() => {
                handleClick();
                eventCollection?.submitAnswer &&
                  eventCollection?.submitAnswer();
              }}
              disabled={
                loading ||
                !userAttemptedAnswer ||
                userAttemptedAnswer.length === 0
              }
            >
              Submit
            </PrimaryButton>
          )}
        </Box>
      )}
    </Box>
  );
}
